import { getCatalogEntry, type ToastMessageContext } from './toast-catalog';
import { normalizePortugueseText } from './text-normalizer';

export { normalizePortugueseText } from './text-normalizer';

const TECHNICAL_PATTERNS: RegExp[] = [
  /\b(QueryFailedError|AxiosError|TypeError|SyntaxError|ReferenceError)\b/gi,
  /\b(SELECT|INSERT|UPDATE|DELETE|FROM|WHERE|JOIN|CONSTRAINT|COLUMN|RELATION|TABLE)\b/gi,
  /\b(ECONNREFUSED|EADDRINUSE|ETIMEDOUT|ERR_[A-Z_]+)\b/gi,
  /\b\d{2,5}[A-Z]\d{2,5}\b/gi,
  /\b[A-Z]{2,6}\d{2,5}\b/gi,
  /\bcode:\s*['"]?[A-Z0-9_:-]+['"]?/gi,
  /\bstatus code\s*\d{3}\b/gi,
  /\bSQLSTATE\s*[A-Z0-9]+\b/gi,
  /at\s+[^\n\r]+/gi,
  /[A-Za-z]:\\[^\s]+/g,
  /\/(?:src|dist|node_modules)\/[^\s)]+/g,
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi,
];

const GENERIC_TECHNICAL_TEXTS = [
  'request failed with status code',
  'request failed with',
  'internal server error',
  'network error',
  'failed to fetch',
  'cannot read properties',
  'unexpected token',
  'query failed',
  'something went wrong',
  'invalid input syntax',
  'duplicate key',
  'null value',
  'violates',
  'timeout',
  'unauthorized',
  'forbidden',
  'not found',
  'bad request',
  'conflict',
];

const ENGLISH_TO_PORTUGUESE: Array<[RegExp, string]> = [
  [/request failed with status code\s*400/i, 'N\u00e3o foi poss\u00edvel concluir a solicita\u00e7\u00e3o.'],
  [/request failed with status code\s*401/i, 'Sua sess\u00e3o expirou ou voc\u00ea n\u00e3o tem permiss\u00e3o para esta a\u00e7\u00e3o.'],
  [/request failed with status code\s*403/i, 'Voc\u00ea n\u00e3o tem permiss\u00e3o para esta a\u00e7\u00e3o.'],
  [/request failed with status code\s*404/i, 'N\u00e3o foi poss\u00edvel encontrar o recurso solicitado.'],
  [/request failed with status code\s*409/i, 'N\u00e3o foi poss\u00edvel concluir por conflito de dados.'],
  [/request failed with status code\s*422/i, 'Os dados informados s\u00e3o inv\u00e1lidos.'],
  [/request failed with status code\s*500/i, 'Ocorreu uma instabilidade no servidor.'],
  [/request failed with/i, 'N\u00e3o foi poss\u00edvel concluir a solicita\u00e7\u00e3o.'],
  [/network error|failed to fetch/i, 'N\u00e3o foi poss\u00edvel conectar ao servidor.'],
  [/timeout|timed out/i, 'A solicita\u00e7\u00e3o demorou mais que o esperado.'],
  [/forbidden|unauthorized/i, 'Voc\u00ea n\u00e3o tem permiss\u00e3o para esta a\u00e7\u00e3o.'],
  [/not found/i, 'N\u00e3o foi poss\u00edvel encontrar o recurso solicitado.'],
  [/duplicate key|already exists/i, 'J\u00e1 existe um registro com os mesmos dados.'],
  [/null value|violates|constraint|foreign key/i, 'N\u00e3o foi poss\u00edvel concluir por dados inconsistentes.'],
  [/invalid input|syntax error/i, 'Os dados informados s\u00e3o inv\u00e1lidos.'],
];

function extractErrorText(errorOrText: unknown): string {
  if (typeof errorOrText === 'string') {
    return errorOrText;
  }

  if (Array.isArray(errorOrText)) {
    return errorOrText
      .map((item) => extractErrorText(item))
      .filter(Boolean)
      .join(' ');
  }

  if (errorOrText instanceof Error) {
    return errorOrText.message || '';
  }

  if (!errorOrText || typeof errorOrText !== 'object') {
    return '';
  }

  const source = errorOrText as any;
  const responseData = source?.response?.data ?? source?.data?.response?.data;

  const extractFromPayload = (payload: unknown): string => {
    if (payload == null) return '';
    if (typeof payload === 'string') {
      const normalized = payload.trim();
      if (!normalized) return '';

      try {
        const parsed = JSON.parse(normalized);
        const parsedMessage = extractFromPayload(parsed);
        return parsedMessage || normalized;
      } catch {
        return normalized;
      }
    }

    if (Array.isArray(payload)) {
      return payload
        .map((item) => extractFromPayload(item))
        .filter(Boolean)
        .join(' ');
    }

    if (typeof payload === 'object') {
      const objectPayload = payload as Record<string, unknown>;

      const candidates = [
        objectPayload['message'],
        objectPayload['error'],
        objectPayload['detail'],
        objectPayload['title'],
      ];

      for (const candidate of candidates) {
        const extracted = extractFromPayload(candidate);
        if (extracted) return extracted;
      }

      if (Array.isArray(objectPayload['errors'])) {
        const extracted = extractFromPayload(objectPayload['errors']);
        if (extracted) return extracted;
      }
    }

    return '';
  };

  const responseMessage = extractFromPayload(responseData);
  if (responseMessage) return responseMessage;

  return source?.message || '';
}

function translateCommonMessage(value: string): string {
  const normalized = normalizePortugueseText(value);
  for (const [pattern, replacement] of ENGLISH_TO_PORTUGUESE) {
    if (pattern.test(normalized)) {
      return replacement;
    }
  }

  return normalized;
}

function sanitizeTechnicalMessage(raw: string): string {
  const normalized = normalizePortugueseText(raw);

  // Try direct translation first — before stripping patterns — so status codes are intact
  for (const [pattern, replacement] of ENGLISH_TO_PORTUGUESE) {
    if (pattern.test(normalized)) {
      return replacement;
    }
  }

  let sanitized = normalized;
  for (const pattern of TECHNICAL_PATTERNS) {
    sanitized = sanitized.replace(pattern, ' ');
  }

  sanitized = sanitized
    .replace(/[{}[\]<>]/g, ' ')
    .replace(/\b(stack|trace|exception|driver|routine|schema|position|detail|hint)\b/gi, ' ')
    .replace(/--\s*parameters:[^.]+/gi, ' ')
    .replace(/parameters?:\s*\[[^\]]*]/gi, ' ')
    .replace(/["']{2,}/g, ' ')
    .replace(/\(\s*\)/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();

  return translateCommonMessage(sanitized);
}

function buildActionHint(context?: ToastMessageContext): string {
  const entry = getCatalogEntry(context);
  return entry.actionHint;
}

function looksTechnicalOrGeneric(text: string): boolean {
  const normalized = text.toLowerCase();
  if (!normalized) return true;
  if (normalized.length < 4) return true;
  if (GENERIC_TECHNICAL_TEXTS.some((item) => normalized.includes(item))) return true;
  return TECHNICAL_PATTERNS.some((p) => new RegExp(p.source, 'i').test(normalized));
}

function ensureFinalPunctuation(text: string): string {
  if (!text) return text;
  return /[.!?]$/.test(text) ? text : `${text}.`;
}

function looksMeaningful(text: string): boolean {
  // After stripping technical patterns, any remaining text that should be shown
  // to users will be in Portuguese (translated or originally) — so it should
  // contain at least one accented/Portuguese letter.
  return /[áàâãéêíóôõúüçÁÀÂÃÉÊÍÓÔÕÚÜÇ]/.test(text);
}

export function extractFriendlyErrorMessage(input: unknown): string | undefined {
  const source = input as any;
  const backendRawMessage =
    source?.response?.data?.message ??
    source?.response?.data?.error?.message ??
    source?.data?.response?.data?.message ??
    source?.data?.response?.data?.error?.message;

  if (backendRawMessage !== undefined) {
    const backendMessage = extractErrorText(backendRawMessage);
    const normalizedBackendMessage = normalizePortugueseText(backendMessage);
    if (normalizedBackendMessage && !looksTechnicalOrGeneric(normalizedBackendMessage)) {
      return ensureFinalPunctuation(normalizedBackendMessage);
    }
  }

  const raw = extractErrorText(input);
  const normalizedRaw = normalizePortugueseText(raw);

  if (normalizedRaw && !looksTechnicalOrGeneric(normalizedRaw)) {
    return ensureFinalPunctuation(normalizedRaw);
  }

  const sanitized = sanitizeTechnicalMessage(raw);
  if (sanitized && looksMeaningful(sanitized) && !looksTechnicalOrGeneric(sanitized)) {
    return ensureFinalPunctuation(sanitized);
  }

  return undefined;
}

export function toPortugueseFriendlyMessage(
  input: unknown,
  context?: ToastMessageContext,
  fallback?: string,
): string {
  const entry = getCatalogEntry(context);
  const defaultTitle = normalizePortugueseText(entry.title);
  const fallbackSanitized = sanitizeTechnicalMessage(fallback || defaultTitle);
  const fallbackTitle = looksTechnicalOrGeneric(fallbackSanitized)
    ? defaultTitle
    : fallbackSanitized;

  const raw = extractErrorText(input);
  const sanitized = sanitizeTechnicalMessage(raw);

  const title = (looksTechnicalOrGeneric(sanitized) || !looksMeaningful(sanitized)) ? fallbackTitle : sanitized;
  return ensureFinalPunctuation(title);
}

export function resolveToastMessage(
  errorOrText: unknown,
  fallback?: string,
  context?: ToastMessageContext,
): string {
  const directFriendly = extractFriendlyErrorMessage(errorOrText);
  if (directFriendly) {
    return directFriendly;
  }

  const title = toPortugueseFriendlyMessage(errorOrText, context, fallback);
  const actionHint = buildActionHint(context);

  const hint = ensureFinalPunctuation(normalizePortugueseText(actionHint));
  const hasHint = title.toLowerCase().includes(hint.toLowerCase());

  if (!hint || hasHint) {
    return title;
  }

  return `${title} ${hint}`;
}
